import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  MenuItem,
  Pagination,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { getApiErrorMessage } from "../../../utils/apiError";
import { colors } from "../../../theme/colors";

interface AuditLog {
  id: string;
  logId: string;
  timestamp: string;
  user: string;
  role: string;
  action: string;
  type: string;
  resource: string;
  ipAddress: string;
  status: "Success" | "Failed";
  details: string;
}

interface AuditLogApiItem {
  id?: string;
  log_id?: string;
  user_name?: string;
  user_email?: string;
  user_role?: string;
  action?: string;
  type?: string;
  resource?: string;
  ip?: string | null;
  status?: string;
  details?: string;
  request?: string;
  response?: string;
  created_at?: string;
}

interface AuditLogApiResponse {
  items?: AuditLogApiItem[];
  total?: number;
  page?: number;
  page_size?: number;
  pages?: number;
  action_type?: string | null;
  status?: string | null;
  actor?: string | null;
  resource?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

const ROWS_PER_PAGE = 20;

const toTitleCase = (value?: string) => {
  if (!value) {
    return "--";
  }

  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeStatus = (value?: string): AuditLog["status"] =>
  value?.toLowerCase() === "success" ? "Success" : "Failed";

const normalizeAuditLog = (item: AuditLogApiItem, index: number): AuditLog => ({
  id: item.id || item.log_id || `audit-log-${index + 1}`,
  logId: item.log_id || `LOG-${index + 1}`,
  timestamp: formatTimestamp(item.created_at),
  user: item.user_name || "--",
  role: toTitleCase(item.user_role),
  action: item.action || "--",
  type: toTitleCase(item.type),
  resource: toTitleCase(item.resource),
  ipAddress: item.ip || "--",
  status: normalizeStatus(item.status),
  details: item.details || "--",
});

const getAuditLogFilterParams = ({
  actionType,
  status,
  user,
  resource,
  dateFrom,
  dateTo,
}: {
  actionType: string;
  status: string;
  user: string;
  resource: string;
  dateFrom: string;
  dateTo: string;
}) => ({
  action_type: actionType === "ALL" ? undefined : actionType.toLowerCase(),
  status:
    status === "ALL"
      ? undefined
      : status.toLowerCase() === "failed"
        ? "fail"
        : status.toLowerCase(),
  actor: user.trim() || undefined,
  resource: resource.trim() || undefined,
  date_from: dateFrom || undefined,
  date_to: dateTo || undefined,
});

const getAuditLogExportData = (items: AuditLog[]) =>
  items.map((log) => ({
    "Log ID": log.logId,
    Timestamp: log.timestamp,
    User: log.user,
    Role: log.role,
    Action: log.action,
    Type: log.type,
    Resource: log.resource,
    "IP Address": log.ipAddress,
    Status: log.status,
    Details: log.details,
  }));

const AuditLogs: React.FC = () => {
  const maxDate = getTodayDateInputValue();
  const [actionType, setActionType] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");
  const [user, setUser] = useState<string>("");
  const [resource, setResource] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>("");
  const [appliedDateTo, setAppliedDateTo] = useState<string>("");
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const hasPendingDateChanges =
    Boolean(dateFrom && dateTo) &&
    (dateFrom !== appliedDateFrom || dateTo !== appliedDateTo);

  const applyDateFilters = () => {
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const filterCardSx = {
    borderRadius: "10px",
    border: "1px solid #D1D5DB",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.14)",
    overflow: "hidden",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 42,
      borderRadius: "6px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#D1D5DB",
      },
      "&:hover fieldset": {
        borderColor: "#C4CBD5",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#9CA3AF",
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input": {
      fontSize: "13px",
      color: "#374151",
      py: 0,
    },
    "& .MuiInputLabel-root": {
      fontSize: "12px",
    },
  };

  const filterSelectSx = {
    ...inputSx,
    "& .MuiSelect-select": {
      fontSize: "13px",
      color: "#4B5563",
      display: "flex",
      alignItems: "center",
      minHeight: "36px",
      py: "0 !important",
    },
  };

  useEffect(() => {
    setPage(1);
  }, [actionType, status, user, resource, appliedDateFrom, appliedDateTo]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchAuditLogs = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get<AuditLogApiResponse>(
          endpoints.dashboard.activityLogs,
          {
            params: {
              ...getAuditLogFilterParams({
                actionType,
                status,
                user,
                resource,
                dateFrom: appliedDateFrom,
                dateTo: appliedDateTo,
              }),
              page,
              page_size: ROWS_PER_PAGE,
            },
            signal: controller.signal,
          },
        );

        const items = Array.isArray(response.data?.items)
          ? response.data.items
          : [];

        setLogs(items.map((item, index) => normalizeAuditLog(item, index)));
        setTotalCount(response.data?.total || items.length);
        setTotalPages(
          Math.max(
            1,
            response.data?.pages ||
              Math.ceil((response.data?.total || items.length) / ROWS_PER_PAGE),
          ),
        );
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setErrorMessage(
          getApiErrorMessage(
            error,
            "Failed to load activity logs. Please try again.",
          ),
        );
        setLogs([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchAuditLogs();

    return () => {
      controller.abort();
    };
  }, [
    actionType,
    appliedDateFrom,
    appliedDateTo,
    page,
    resource,
    status,
    user,
  ]);

  const uniqueActionTypes = useMemo(
    () => ["ALL", ...Array.from(new Set(logs.map((log) => log.type)))],
    [logs],
  );

  const handleClearFilters = () => {
    setActionType("ALL");
    setStatus("ALL");
    setUser("");
    setResource("");
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setPage(1);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    try {
      const response = await api.get<AuditLogApiResponse>(
        endpoints.dashboard.activityLogsExport,
        {
          params: getAuditLogFilterParams({
            actionType,
            status,
            user,
            resource,
            dateFrom: appliedDateFrom,
            dateTo: appliedDateTo,
          }),
        },
      );

      const items = Array.isArray(response.data?.items)
        ? response.data.items
        : [];
      const exportLogs = items.map((item, index) =>
        normalizeAuditLog(item, index),
      );
      const exportData = getAuditLogExportData(exportLogs);

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const columnWidths = [
        { wch: 10 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
        { wch: 30 },
        { wch: 12 },
        { wch: 25 },
        { wch: 15 },
        { wch: 10 },
        { wch: 50 },
      ];
      worksheet["!cols"] = columnWidths;

      // Style the header row
      const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1:J1");
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[address]) continue;
        worksheet[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "3B82F6" } },
          alignment: { horizontal: "center" },
        };
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Logs");

      // Add summary sheet
      const summaryData = [
        ["Report Generated", new Date().toLocaleString()],
        ["Total Records", response.data?.total || exportLogs.length],
        ["Exported Records", exportLogs.length],
        [
          "Success Records",
          exportLogs.filter((log) => log.status === "Success").length,
        ],
        [
          "Failed Records",
          exportLogs.filter((log) => log.status === "Failed").length,
        ],
        [
          "Create Actions",
          exportLogs.filter((log) => log.type === "Create").length,
        ],
        [
          "Update Actions",
          exportLogs.filter((log) => log.type === "Update").length,
        ],
        [
          "Delete Actions",
          exportLogs.filter((log) => log.type === "Delete").length,
        ],
        [
          "Export Actions",
          exportLogs.filter((log) => log.type === "Export").length,
        ],
        [
          "Access Actions",
          exportLogs.filter((log) => log.type === "Access").length,
        ],
        [
          "Login Actions",
          exportLogs.filter((log) => log.type === "Login").length,
        ],
        [
          "Logout Actions",
          exportLogs.filter((log) => log.type === "Logout").length,
        ],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      // Generate filename
      const fileName = `Audit_Logs_${new Date().toISOString().split("T")[0]}.xlsx`;

      // Export to Excel
      XLSX.writeFile(workbook, fileName);
    } catch (error: any) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "Failed to export activity logs. Please try again.",
        ),
      );
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    return status === "Success" ? "#10B981" : "#EF4444";
  };

  const getStatusChip = (status: string) => {
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: `${getStatusColor(status)}15`,
          color: getStatusColor(status),
          fontWeight: 600,
          fontSize: "10px",
          borderRadius: "999px",
          height: 22,
        }}
      />
    );
  };

  const getActionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      Create: "#4CAF50",
      Update: "#1E88E5",
      Delete: "#EF5350",
      Export: "#EC4899",
      Access: "#14B8A6",
      Login: "#A855F7",
      Logout: "#FB923C",
    };
    return colors[type] || "#6B7280";
  };

  const getLogIdParts = (id: string) => {
    const [prefix, suffix] = id.split("-");

    if (!suffix) {
      return { prefix: id, suffix: "" };
    }

    return {
      prefix: `${prefix}-`,
      suffix,
    };
  };

  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh", py: 3 }}>
      <Container maxWidth="xl">
        <Paper sx={{ ...filterCardSx, mb: 3 }}>
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#232323",
                mb: 1.7,
              }}
            >
              Filter & Search
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "1.15fr 1.15fr 1fr 1fr 1fr 1fr",
                },
                gap: 1.5,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search User"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <SearchIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                    ),
                  },
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                size="small"
                placeholder="Search Resource"
                value={resource}
                onChange={(e) => setResource(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <SearchIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                    ),
                  },
                }}
                sx={inputSx}
              />

              <FormControl fullWidth size="small" sx={filterSelectSx}>
                <Select
                  value={actionType}
                  displayEmpty
                  onChange={(e: SelectChangeEvent) =>
                    setActionType(e.target.value)
                  }
                  renderValue={(selected) =>
                    selected === "ALL" ? "All Actions" : selected
                  }
                >
                  {uniqueActionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === "ALL" ? "All Actions" : type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={filterSelectSx}>
                <Select
                  value={status}
                  displayEmpty
                  onChange={(e: SelectChangeEvent) => setStatus(e.target.value)}
                  renderValue={(selected) =>
                    selected === "ALL" ? "Status" : selected
                  }
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="Success">Success</MenuItem>
                  <MenuItem value="Failed">Failed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "Start date",
                    max: maxDate,
                  },
                }}
                sx={inputSx}
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    "aria-label": "End date",
                    max: maxDate,
                  },
                }}
                sx={inputSx}
              />
            </Box>

            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                Date filters update the table only after you click Apply Dates.
              </Typography>

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={applyDateFilters}
                disabled={!hasPendingDateChanges}
                sx={{
                  minHeight: 36,
                  px: 2,
                  textTransform: "none",
                  borderRadius: "8px",
                  bgcolor: colors.primary,
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: colors.primary,
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#D1D5DB",
                    color: "#6B7280",
                  },
                }}
              >
                Apply Dates
              </Button>
            </Box>
          </Box>
        </Paper>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Paper
          sx={{
            borderRadius: "10px",
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #F1F5F9",
              bgcolor: "white",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#1F2937",
                }}
              >
                Activity Log Records
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isExporting}
              sx={{
                textTransform: "none",
                minHeight: 32,
                px: 1.5,
                borderColor: "#7FBF86",
                color: "#4B9F51",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "12px",
                "&:hover": {
                  borderColor: "#4B9F51",
                  bgcolor: "#F0FDF4",
                  color: "#3F8F46",
                },
              }}
            >
              {isExporting ? "Exporting..." : "Export Logs"}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F7F7F7" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Log ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Timestamp
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    User
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Action
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Resource
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    IP Address
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "13px",
                      color: colors.black,
                      fontFamily: "'Inter', sans-serif",
                      borderBottom: "1px solid #E7E5E4",
                      py: 1.5,
                    }}
                  >
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Box
                        sx={{
                          py: 7,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={30} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow
                      key={log.id}
                      hover
                      sx={{
                        "& td": { borderBottom: "1px solid #F1F5F9", py: 1.55 },
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#4CAF50",
                              fontWeight: 700,
                              lineHeight: 1.1,
                            }}
                          >
                            {getLogIdParts(log.logId).prefix}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "#4CAF50",
                              fontWeight: 700,
                              lineHeight: 1.1,
                            }}
                          >
                            {getLogIdParts(log.logId).suffix}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#78716C",
                            lineHeight: 1.45,
                          }}
                        >
                          {log.timestamp.split(" ")[0]}
                          <br />
                          {log.timestamp.split(" ")[1] || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#292524",
                              lineHeight: 1.25,
                            }}
                          >
                            {log.user}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "9px",
                              color: "#8A8A8A",
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {log.role}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#44403C",
                            maxWidth: 150,
                            lineHeight: 1.35,
                          }}
                        >
                          {log.action}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.type}
                          size="small"
                          sx={{
                            bgcolor: `${getActionTypeColor(log.type)}15`,
                            color: getActionTypeColor(log.type),
                            fontWeight: 600,
                            fontSize: "10px",
                            borderRadius: "999px",
                            height: 22,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#78716C",
                            maxWidth: 110,
                            lineHeight: 1.35,
                          }}
                        >
                          {log.resource}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "10px",
                            color: "#9CA3AF",
                          }}
                        >
                          {log.ipAddress}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(log.status)}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#78716C",
                            maxWidth: 220,
                            lineHeight: 1.4,
                          }}
                        >
                          {log.details}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLoading && logs.length > 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                borderTop: "1px solid #E2E8F0",
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#8A8A8A" }}>
                Showing {logs.length} of {totalCount} total records
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  count={Math.max(1, totalPages)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              )}
            </Box>
          )}

          {!isLoading && logs.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" sx={{ color: "#64748B" }}>
                No audit logs found matching your criteria
              </Typography>
              <Button
                onClick={handleClearFilters}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AuditLogs;
